import type React from "react";

import { cn } from "../lib/utils";
import { Gift, ImageIcon } from "./icons";

export interface ProductCardProps {
  title: string;
  /** Short spec line under the title, e.g. «۲۴ عیار · آرسس گلد». */
  subtitle?: string;
  imageUrl?: string;
  imageAlt?: string;
  /** The formatted price, or the sales-halt text. */
  priceLabel: string;
  /** No price right now (sales halted, F17): the price line is muted. */
  priceHalted?: boolean;
  /** Extra classes on the price line, e.g. a brief highlight when it changes. */
  priceClassName?: string;
  stock: number;
  /** Top-corner badge on the photo, e.g. the weight. */
  badge?: string;
  /** A festival gift with this bar (2.29): its name, shown on the photo. */
  gift?: string | null;
  footer?: React.ReactNode;
}

// App-style product tile, like the home bento cards: white surface, 24px
// corners, the bar's photo on a soft surface, then title, specs, price and
// «ناموجود» when sold out. Shared by landing's /store and the /app dashboard. Photos fill the
// square (catalog shots are on their own light backdrop).
export function ProductCard({
  title,
  subtitle,
  imageUrl,
  imageAlt,
  priceLabel,
  priceHalted = false,
  priceClassName,
  stock,
  badge,
  gift,
  footer,
}: ProductCardProps) {
  const inStock = stock > 0;

  return (
    <div className="bg-background flex h-full flex-col overflow-hidden rounded-card shadow-sm">
      <div className="bg-surface relative aspect-square w-full">
        {imageUrl ? (
          // Catalog photos are already 1200px WebP in the public bucket.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageUrl}
            alt={imageAlt ?? title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <ImageIcon className="text-muted-foreground absolute inset-0 m-auto opacity-50" size={28} />
        )}
        {badge ? (
          <span className="bg-background/90 absolute start-2.5 top-2.5 rounded-full px-2.5 py-1 text-xs font-semibold">
            {badge}
          </span>
        ) : null}
        {gift ? (
          <span className="bg-tint-yellow text-ink-yellow absolute start-2.5 bottom-2.5 inline-flex max-w-[calc(100%-1.25rem)] items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold">
            <Gift size={13} className="shrink-0" />
            <span className="truncate">هدیه: {gift}</span>
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-3 sm:p-4">
        <h3 className="line-clamp-2 text-sm leading-6 font-semibold">{title}</h3>
        {subtitle ? <p className="text-muted-foreground text-xs">{subtitle}</p> : null}
        <div className="mt-auto flex flex-col gap-0.5 pt-2">
          <span
            className={cn(
              "rounded-md text-sm font-bold transition-colors duration-700 sm:text-base",
              priceHalted && "text-muted-foreground text-xs font-medium sm:text-sm",
              priceClassName,
            )}
          >
            {priceLabel}
          </span>
          {/* In stock is the norm; only a sold-out bar is marked. */}
          {inStock ? null : <span className="text-muted-foreground text-xs">ناموجود</span>}
        </div>
        {footer}
      </div>
    </div>
  );
}
